/**
 * Auth provider that completes the login redirect through React Router.
 *
 * What: Wraps `DevAuthProvider` and wires Auth0's `onRedirectCallback` to
 * `useNavigate`, so `loginWithRedirect({ appState: { returnTo } })` is the one
 * post-login destination mechanism app-wide.
 * Why: The SDK's default callback rewrites the URL with raw
 * `history.replaceState`, which React Router never observes — `appState.returnTo`
 * was silently dropped and users were bounced to the landing page.
 * Also carries explicit access-code-page logout intent across the logout
 * round trip, requesting account selection until the next successful login.
 * When: Update when changing the post-login navigation contract or `/callback`
 * handling (see `AuthCallback.tsx` for the no-returnTo role-default path).
 *
 * Must render inside `BrowserRouter` (it calls `useNavigate`).
 */
import { type ReactNode, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type AppState,
  Auth0Context,
  type Auth0ContextInterface,
  useAuth0,
} from '@auth0/auth0-react';

import { DevAuthProvider, type DevAuthProviderProps } from './DevAuthProvider';
import {
  clearLogoutAccountSelection,
  recordLogoutAccountSelection,
  withLogoutAccountSelection,
} from './logoutAccountSelection';

type Auth0ProviderWithNavigateProps = Omit<
  DevAuthProviderProps,
  'onRedirectCallback'
>;

/** Covers every useAuth0 login entry point, including the public landing page. */
function AccountSelectionAfterLogout({ children }: { children: ReactNode }) {
  const auth0 = useAuth0();
  const { loginWithRedirect: sdkLogin, logout: sdkLogout } = auth0;
  const loginWithRedirect = useCallback<
    Auth0ContextInterface['loginWithRedirect']
  >(
    options => {
      const loginOptions = withLogoutAccountSelection(options);
      return loginOptions === undefined ? sdkLogin() : sdkLogin(loginOptions);
    },
    [sdkLogin]
  );
  const logout = useCallback<Auth0ContextInterface['logout']>(
    async options => {
      recordLogoutAccountSelection(window.location.pathname);
      try {
        await sdkLogout(options);
      } catch (error) {
        clearLogoutAccountSelection();
        throw error;
      }
    },
    [sdkLogout]
  );
  const value = useMemo(
    () => ({ ...auth0, loginWithRedirect, logout }),
    [auth0, loginWithRedirect, logout]
  );

  return (
    <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
  );
}

export const Auth0ProviderWithNavigate = ({
  children,
  ...auth0ProviderProps
}: Auth0ProviderWithNavigateProps) => {
  const navigate = useNavigate();

  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      clearLogoutAccountSelection();
      // With a returnTo, go straight there (protected routes handle the rest).
      // Without one, stay on /callback minus the code/state params so
      // AuthCallback can resolve the role-default destination once the role
      // loads — the role isn't known yet at exchange time.
      navigate(appState?.returnTo || window.location.pathname, {
        replace: true,
      });
    },
    [navigate]
  );

  return (
    <DevAuthProvider
      {...auth0ProviderProps}
      onRedirectCallback={onRedirectCallback}
    >
      <AccountSelectionAfterLogout>{children}</AccountSelectionAfterLogout>
    </DevAuthProvider>
  );
};
